/**
 * A lista única da aba Documentos (E205.F4.T02/T04, ADR-0147 §2.5): uma linha por documento, a CCB
 * primeiro, e, como linhas também, o que falta no conjunto enviado (o kit incompleto) e o arquivo
 * único cuja separação não fechou. Essas duas levam ao envio, no alto da aba.
 *
 * Absorve a lista do painel, "Documentos de apoio", a leitura das imagens de documento (que estava em
 * Fotografias), o aviso do kit incompleto e o do arquivo único; a situação de cada página, que a
 * ficha mostrava, é a coluna "Páginas e leitura".
 *
 * E207.F6.T01 (owner, 01/11): a lista é a tabela do design system (`Table`, ADR-0065): o cabeçalho
 * das cinco colunas e um grupo de linhas por documento (`LinhaDeDocumento`), por tipo que falta no
 * kit e por arquivo único que não separou; as ações numa coluna só, à direita. Abaixo de `md` a
 * tabela rola na horizontal.
 *
 * E216.F4.C03 (GUS-317 P6): os arquivos excluídos vêm por último, sem letra, com o motivo, quem
 * excluiu e quando (`LinhaDoExcluido`).
 */
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ArquivoUnicoRecebidoData } from '@/lib/exame_eletronico_arquivo_unico';
import type { ExcluidoNaMesaData } from '@/lib/excluidos_na_mesa';

import { COLUNAS_DA_TABELA, LinhaDeDocumento, NomeNaTabela } from './LinhaDeDocumento';
import { LinhaDoExcluido } from './LinhaDoExcluido';
import type { LinhaDoDocumento } from './linhasDosDocumentos';

const t = ptBR.documentosDoCaso;

function Cabecalho(): JSX.Element {
  const c = t.colunas;
  return (
    <TableHeader>
      <TableRow>
        <TableHead>{c.letra}</TableHead>
        <TableHead>{c.documento}</TableHead>
        <TableHead>{c.papel}</TableHead>
        <TableHead>{c.paginas}</TableHead>
        <TableHead>{c.custodia}</TableHead>
        <TableHead>{c.usado}</TableHead>
        <TableHead className="text-right">{c.acoes}</TableHead>
      </TableRow>
    </TableHeader>
  );
}

/**
 * Owner, 04/11: a última linha conta os arquivos carregados (os vivos e os excluídos) e os
 * utilizados no exame (os que a seção 2 lista); sem a seção 2 composta, os utilizados ficam "—".
 */
function Contagem({
  carregados,
  utilizados,
}: {
  carregados: number;
  utilizados: number | null;
}): JSX.Element {
  const usados =
    utilizados === null
      ? t.contagem.semExame
      : t.contagem.utilizados.replace('{n}', String(utilizados));
  return (
    <TableFooter data-testid="documentos-contagem">
      <TableRow className="hover:bg-transparent">
        <TableCell colSpan={COLUNAS_DA_TABELA} className="text-body-sm font-medium text-fg">
          <span data-testid="contagem-carregados">
            {t.contagem.carregados.replace('{n}', String(carregados))}
          </span>
          <span aria-hidden="true" className="px-sc-2 text-fg-muted">
            ·
          </span>
          <span data-testid="contagem-utilizados">{usados}</span>
        </TableCell>
      </TableRow>
    </TableFooter>
  );
}

/** A linha do que falta (o kit) ou do arquivo que não separou: nome, papel, a situação, o envio. */
function LinhaQueFalta({
  nome,
  papel,
  situacao,
  detalhes,
  testId,
  onEnviar,
}: {
  nome: string;
  papel: string;
  situacao: string;
  detalhes?: readonly string[];
  testId: string;
  onEnviar: () => void;
}): JSX.Element {
  return (
    <TableBody data-testid={testId}>
      <TableRow>
        <TableCell className="text-body-sm text-fg-muted">{t.semLetra}</TableCell>
        <TableCell>
          <NomeNaTabela nome={nome} />
          {detalhes !== undefined && detalhes.length > 0 ? (
            <ul className="mt-sc-1 list-disc pl-sc-4 text-caption text-fg-muted">
              {detalhes.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          ) : null}
        </TableCell>
        <TableCell className="text-body-sm text-fg-muted">{papel}</TableCell>
        <TableCell className="text-body-sm text-warning-700">{situacao}</TableCell>
        <TableCell className="text-body-sm text-fg-muted">{t.semCustodia}</TableCell>
        <TableCell className="text-body-sm text-fg-muted">{t.usado.semExame}</TableCell>
        <TableCell className="whitespace-nowrap text-right">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onEnviar}
            data-testid={`${testId}-enviar`}
          >
            {t.enviar}
          </Button>
        </TableCell>
      </TableRow>
    </TableBody>
  );
}

export interface ListaDeDocumentosProps {
  caseId: string;
  linhas: readonly LinhaDoDocumento[];
  /** Os nomes dos tipos que faltam no conjunto enviado (o kit incompleto). */
  faltantes: readonly string[];
  /** Os arquivos únicos cuja separação não fechou. */
  arquivosComFalha: readonly ArquivoUnicoRecebidoData[];
  /** E216.F4.C03: os arquivos excluídos, listados no fim. */
  excluidos?: readonly ExcluidoNaMesaData[];
  /** Owner, 04/11: a seção 2 do exame foi composta (a coluna e a contagem do uso afirmam). */
  exameComposto?: boolean;
  concluido: boolean;
  onConfirmado: () => void;
  onEnviar: () => void;
  /** E214.F1 (GUS-314): o recorte manual de uma linha. */
  onRecortar?: ((linha: LinhaDoDocumento) => void) | undefined;
  /** E216.F4 (GUS-317 item 3): a exclusão de uma linha, com motivo e fundamento. */
  onExcluir?: ((linha: LinhaDoDocumento) => void) | undefined;
}

export function ListaDeDocumentos({
  caseId,
  linhas,
  faltantes,
  arquivosComFalha,
  excluidos = [],
  exameComposto = false,
  concluido,
  onConfirmado,
  onEnviar,
  onRecortar,
  onExcluir,
}: ListaDeDocumentosProps): JSX.Element {
  const total = linhas.length + faltantes.length + arquivosComFalha.length + excluidos.length;
  if (total === 0) {
    return (
      <p className="text-body-sm text-fg-muted" data-testid="documentos-vazio">
        {t.vazio}
      </p>
    );
  }
  return (
    <section aria-label={t.rotulo} className="overflow-x-auto" data-testid="lista-de-documentos">
      <Table>
        <Cabecalho />
        {linhas.map((linha) => (
          <LinhaDeDocumento
            key={linha.id}
            caseId={caseId}
            linha={linha}
            concluido={concluido}
            exameComposto={exameComposto}
            onConfirmado={onConfirmado}
            onRecortar={onRecortar}
            onExcluir={onExcluir}
          />
        ))}
        {faltantes.map((tipo) => (
          <LinhaQueFalta
            key={`kit-${tipo}`}
            nome={t.kit.rotulo.replace('{tipo}', tipo)}
            papel={tipo}
            situacao={t.kit.falta}
            testId="linha-do-kit"
            onEnviar={onEnviar}
          />
        ))}
        {arquivosComFalha.map((arquivo) => (
          <LinhaQueFalta
            key={`arquivo-${arquivo.documento_id}`}
            nome={arquivo.nome_do_arquivo}
            papel={t.papel.arquivoUnico}
            situacao={t.arquivoUnico.falhou}
            detalhes={arquivo.separacao.motivos.map((m) => m.detalhe)}
            testId="linha-do-arquivo-unico"
            onEnviar={onEnviar}
          />
        ))}
        {excluidos.map((excluido) => (
          <LinhaDoExcluido key={`excluido-${excluido.documento_id}`} excluido={excluido} />
        ))}
        <Contagem
          carregados={linhas.length + excluidos.length}
          utilizados={exameComposto ? linhas.filter((l) => l.letra !== null).length : null}
        />
      </Table>
    </section>
  );
}
